import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectClient,
  selectClientLocalCareEnvironmentCosts,
  selectMaybeClientPartner,
} from '@client/model';
import { CarePlanSummary, buildCarePlanSummary } from '../components/carePlanDetails/buildCarePlanSummary';

export interface CarePlanSummaries {
  client: CarePlanSummary;
  /** Null for single-life clients (no linked partner). */
  partner: CarePlanSummary | null;
}

/**
 * Memoized read-only model mirroring the Edit Care Assumptions screen. The
 * build is pure data-transformation, so memoization is just a cache. Returns the
 * client's summary plus the partner's when one exists (joint planning); the
 * partner is a fully-derived `Client`, so it carries its own local costs.
 */
export function useCarePlanSummary(): CarePlanSummaries {
  const client = useSelector(selectClient);
  const localCosts = useSelector(selectClientLocalCareEnvironmentCosts);
  const partner = useSelector(selectMaybeClientPartner);
  return useMemo(
    () => ({
      client: buildCarePlanSummary(client, localCosts),
      partner: partner
        ? buildCarePlanSummary(partner, partner.clientLocalCareEnvironmentCosts)
        : null,
    }),
    [client, localCosts, partner],
  );
}
